import { Injectable } from "@angular/core";
import { AdvancedReportService, CultivarService, DetailRequest, Filter, FilterBuilder, FilterQuery, FilterQueryMode, SiteService, Status, translateCommonErrorStatus } from "@core/data";
import { AdvancedReport, ReportDataFilter, ReportFilterOptions } from "@core/data/types/advanced-report";
import { Action, Selector, State, StateContext } from "@ngxs/store";
import { forkJoin, of } from "rxjs";
import { catchError, tap } from "rxjs/operators";

export interface ReportFilterFormStateModel {
    key: string;
    status: Status;
    data: AdvancedReport;
    filter: Filter;
    licenseeOptions: string[];
}

const DEFAULTS: ReportFilterFormStateModel = {
    key: null,
    status: Status.UNINITIALIZED,
    data: null,
    filter: null,
    licenseeOptions: [],
}

export class InitReportFilterForm {
    static readonly type = "[ReportFilterForm] Init";
    constructor(public key: string) { }
}

export class SubmitReportFilterForm {
    static readonly type = "[ReportFilterForm] Submit";
    constructor(public options: ReportFilterOptions, public isLocked: boolean = false) { }
}

export class SetupReportFilter {
    static readonly type = "[ReportFilterForm] Setup";
    constructor() { }
}


export class CheckCultivarFilter {
    static readonly type = "[ReportFilterForm] Cultivar";
    constructor() { }
}

export class CheckSiteFilter {
    static readonly type = "[ReportFilterForm] Site";
    constructor() { }
}

export class LoadLicenseeOptions {
    static readonly type = "[ReportFilterForm] Licensees";
    constructor() { }
}


@State<ReportFilterFormStateModel>({
    name: 'advanced_report_filter_form',
    defaults: DEFAULTS,
})
@Injectable()
export class ReportFilterFormState {

    @Selector()
    static data(state: ReportFilterFormStateModel) {
        return state.data;
    }

    @Selector()
    static filter(state: ReportFilterFormStateModel) {
        return state.filter;
    }

    @Selector()
    static licenseeOptions(state: ReportFilterFormStateModel) {
        return state.licenseeOptions;
    }

    constructor(
        private _reportService: AdvancedReportService,
        private _cultivarService: CultivarService,
        private _siteService: SiteService,
    ) { }


    @Action(InitReportFilterForm)
    initReportFilterForm(ctx: StateContext<ReportFilterFormStateModel>, action: InitReportFilterForm) {

        ctx.setState({
            ...DEFAULTS,
            key: action.key,
            status: Status.LOADING,
        })

        const detailRequest: DetailRequest = {
            related: [
                'options',
            ],
        };

        return this._reportService.get(action.key, detailRequest)
            .pipe(
                tap(
                    result => {
                        ctx.patchState({
                            data: result,
                        });

                        ctx.dispatch(SetupReportFilter);
                    },
                    error => {
                        ctx.patchState({
                            status: translateCommonErrorStatus(error)
                        });
                    }
                )
            );
    }

    @Action(SubmitReportFilterForm)
    submitReportFilterForm(ctx: StateContext<ReportFilterFormStateModel>, action: SubmitReportFilterForm) {

        const state = ctx.getState();

        const filter = this.getCurrentFilter(state, action.options)

        let reportFilter: ReportDataFilter = {
            page: filter.page,
            sort: filter.sort,
            queries: [],
        }

        filter.queries.forEach(q => {
            let value: string = "";

            if (q.mode === FilterQueryMode.IN) {
                q.value.forEach((val, index) => {
                    if (index == (q.value.length - 1)) value += `${val}`;
                    else value += `${val},`;
                })
            } else value = q.value;

            if (value == null || value === "") return;

            let query: { k: string, v: string, m: FilterQueryMode } = {
                k: q.key,
                v: value,
                m: q.mode,
            };

            reportFilter.queries.push(query);
        });

        ctx.patchState({
            status: Status.LOADING,
            data: {
                ...state.data,
                filter: reportFilter,
            }
        });

        return this._reportService.update(state.key, ctx.getState().data)
            .pipe(
                tap(
                    result => {
                        ctx.patchState({
                            key: result.key,
                            status: Status.COMPLETE,
                            data: result,
                        });

                        // TODO UPDATE OPTION DATA AND CALL NEW API LOCK ROUTE

                    },
                    error => {
                        ctx.patchState({
                            status: translateCommonErrorStatus(error)
                        });
                    }
                )
            )
    }

    @Action(SetupReportFilter)
    setupReportFilter(ctx: StateContext<ReportFilterFormStateModel>, action: SetupReportFilter) {
        const state = ctx.getState();

        const listTypes = [FilterQueryMode.IN, FilterQueryMode.BETWEEN];
        const numbered = ['birthYear', 'birthWeek', 'seasonYear'];
        const numberedSingle = ['seasonMonth'];

        let filter: Filter = {
            page: state.data.filter?.page || null,
            sort: state.data.filter?.sort || null,
            queries: [
                { key: "ownerOrgKey", value: state.data.ownerOrgKey, mode: FilterQueryMode.EQUALS },
            ],
        };

        state.data.filter?.queries.forEach(rawQuery => {
            if (filter.queries.find(q => q.key === rawQuery.k)) return;

            const { k, v, m } = rawQuery;

            let query: FilterQuery = {
                key: k,
                value: v,
                mode: m,
            }

            if (listTypes.includes(query.mode)) query.value = query.value.split(",");
            if (numbered.includes(query.key)) query.value = query.value.map(val => +val);
            if (numberedSingle.includes(query.key)) query.value = +query.value;

            filter.queries.push(query);
        });

        ctx.patchState({
            filter: filter,
            status: Status.LOADING,
        });

        forkJoin([
            ctx.dispatch(CheckCultivarFilter),
            ctx.dispatch(CheckSiteFilter),
            ctx.dispatch(LoadLicenseeOptions),
        ]);
    }

    @Action(CheckCultivarFilter)
    checkCultivarFilter(ctx: StateContext<ReportFilterFormStateModel>, action: CheckCultivarFilter) {
        let state = ctx.getState();

        let cultivarIndex = state.filter?.queries.findIndex(query => query.key === 'scionCultivarKey');

        if (cultivarIndex < 0) {
            ctx.patchState({ status: Status.OK });
            return of(null);
        }

        return this._cultivarService.queryKeys(state.filter.queries[cultivarIndex].value, state.data.ownerOrgKey)
            .pipe(
                tap(
                    result => {
                        state = ctx.getState();
                        let queryCopy = JSON.parse(JSON.stringify(state.filter.queries));

                        queryCopy[cultivarIndex].value = result.data;

                        ctx.patchState({
                            status: Status.OK,
                            filter: {
                                ...state.filter,
                                queries: queryCopy,
                            }
                        });
                    },
                    error => {
                        ctx.patchState({
                            status: translateCommonErrorStatus(error),
                            filter: null,
                            data: null,
                        });
                    }
                )
            );
    }

    @Action(CheckSiteFilter)
    checkSiteFilter(ctx: StateContext<ReportFilterFormStateModel>, action: CheckSiteFilter) {
        let state = ctx.getState();

        let siteIndex = state.filter?.queries.findIndex(query => query.key === 'siteKey');


        if (siteIndex < 0) {
            ctx.patchState({ status: Status.OK });
            return of(null);
        }

        return this._siteService.queryKeys(state.filter.queries[siteIndex].value, state.data.ownerOrgKey)
            .pipe(
                tap(
                    result => {
                        state = ctx.getState();
                        let queryCopy = JSON.parse(JSON.stringify(state.filter.queries));

                        queryCopy[siteIndex].value = result.data;

                        ctx.patchState({
                            status: Status.OK,
                            filter: {
                                ...state.filter,
                                queries: queryCopy,
                            }
                        });
                    },
                    error => {
                        ctx.patchState({
                            status: translateCommonErrorStatus(error),
                            filter: null,
                            data: null,
                        });
                    }
                )
            )
    }

    @Action(LoadLicenseeOptions)
    loadLicenseeOptions(ctx: StateContext<ReportFilterFormStateModel>) {
        const state = ctx.getState();
        const filter = (new FilterBuilder()).setQuery('ownerOrgKey', state.data.ownerOrgKey).get();

        return this._cultivarService.groupLicensees(filter)
            .pipe(
                tap(
                    result => {
                        ctx.patchState({
                            status: Status.OK,
                            licenseeOptions: result.group,
                        });
                    },
                    () => {
                        ctx.patchState({ status: Status.OK, licenseeOptions: [] });
                    }
                )
            );
    }

    private getCurrentFilter(state: ReportFilterFormStateModel, options: ReportFilterOptions): Filter {
        const fb = new FilterBuilder();

        fb.setPage(state.filter.page);
        fb.setSort(state.filter.sort);

        // QUERIES
        fb.setQuery('ownerOrgKey', options.ownerOrgKey, FilterQueryMode.EQUALS);
        fb.setQuery('scionCultivarKey', options.scionCultivarKey, FilterQueryMode.IN);
        fb.setQuery('siteKey', options.siteKey, FilterQueryMode.IN);
        fb.setQuery('scionCropId', options.scionCropId, FilterQueryMode.IN);
        fb.setQuery('birthWeek', options.birthWeek, FilterQueryMode.IN);
        fb.setQuery('birthYear', options.birthYear, FilterQueryMode.IN);
        fb.setQuery('seasonMonth', options.seasonMonth, FilterQueryMode.EQUALS);
        fb.setQuery('seasonYear', options.seasonYear, FilterQueryMode.IN);
        fb.setQuery('scionLicensee', options.licensee, FilterQueryMode.IN);

        if (options.search) {
            if (options.search.trim()) fb.setQuery('$fuzzy', options.search, FilterQueryMode.EQUALS);
        }

        return fb.get();
    }


}